import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { format, formatDistanceToNow, differenceInDays } from "date-fns";
import { AppLayout } from "@/components/AppLayout";
import { TableSkeleton } from "@/components/PageSkeleton";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import {
  apiGetGdprPending, apiGetGdprHistory, apiGdprExtendRetention, apiGdprDeleteNow,
  type GdprPendingApplication, type GdprPendingDocument, type GdprHistoryEntry,
} from "@/lib/api";
import {
  ShieldAlert, ShieldCheck, AlertTriangle, Clock, Trash2, CalendarPlus,
  FileText, UserRound, RefreshCw, Bot, Hand,
} from "lucide-react";

type EntityType = "candidate_application" | "document";

interface ConfirmDelete {
  entityType: EntityType;
  entityId: number;
  label: string;
}

interface ExtendDialog {
  entityType: EntityType;
  entityId: number;
  label: string;
}

export default function GdprRetention() {
  const { user } = useAuth();
  const { toast } = useToast();
  const qc = useQueryClient();

  const [confirmDelete, setConfirmDelete] = useState<ConfirmDelete | null>(null);
  const [extendDialog, setExtendDialog] = useState<ExtendDialog | null>(null);
  const [extendDays, setExtendDays] = useState(30);

  const isHrOrAdmin = user?.role.mainRole === "hr" || user?.role.mainRole === "admin";

  const pendingQuery = useQuery({
    queryKey: ["gdpr-pending"],
    queryFn: apiGetGdprPending,
    enabled: isHrOrAdmin,
  });

  const historyQuery = useQuery({
    queryKey: ["gdpr-history"],
    queryFn: () => apiGetGdprHistory(100),
    enabled: isHrOrAdmin,
  });

  const deleteMutation = useMutation({
    mutationFn: ({ entityType, entityId }: { entityType: EntityType; entityId: number }) =>
      apiGdprDeleteNow(entityType, entityId),
    onSuccess: (data) => {
      toast({
        title: "Deleted",
        description: `Record removed. ${data.filesDeleted} file${data.filesDeleted === 1 ? "" : "s"} cleaned up from storage.`,
      });
      qc.invalidateQueries({ queryKey: ["gdpr-pending"] });
      qc.invalidateQueries({ queryKey: ["gdpr-history"] });
      setConfirmDelete(null);
    },
    onError: (err: any) => {
      toast({
        title: "Delete failed",
        description: err?.message ?? "Could not delete the record.",
        variant: "destructive",
      });
    },
  });

  const extendMutation = useMutation({
    mutationFn: ({ entityType, entityId, days }: { entityType: EntityType; entityId: number; days: number }) =>
      apiGdprExtendRetention(entityType, entityId, days),
    onSuccess: (_, vars) => {
      toast({
        title: "Retention extended",
        description: `Deletion deferred by ${vars.days} day${vars.days === 1 ? "" : "s"}.`,
      });
      qc.invalidateQueries({ queryKey: ["gdpr-pending"] });
      setExtendDialog(null);
      setExtendDays(30);
    },
    onError: (err: any) => {
      toast({
        title: "Extension failed",
        description: err?.message ?? "Could not extend retention.",
        variant: "destructive",
      });
    },
  });

  if (!isHrOrAdmin) {
    return (
      <AppLayout>
        <div className="p-8 text-center">
          <ShieldAlert className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
          <h2 className="text-xl font-semibold mb-2">Access denied</h2>
          <p className="text-muted-foreground">GDPR retention is restricted to HR and Admin roles.</p>
        </div>
      </AppLayout>
    );
  }

  const apps = pendingQuery.data?.candidateApplications ?? [];
  const docs = pendingQuery.data?.documents ?? [];
  const history = historyQuery.data?.rows ?? [];
  const stats = historyQuery.data?.stats;

  const expiredApps = apps.filter((a) => a.expired);
  const expiredDocs = docs.filter((d) => d.expired);
  const upcomingApps = apps.filter((a) => !a.expired);
  const upcomingDocs = docs.filter((d) => !d.expired);

  const totalExpired = expiredApps.length + expiredDocs.length;
  const totalUpcoming = upcomingApps.length + upcomingDocs.length;

  return (
    <AppLayout>
      <div className="space-y-6 w-full">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-xl bg-emerald-500/10 flex items-center justify-center shrink-0">
              <ShieldCheck className="h-5 w-5 text-emerald-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold">GDPR Retention</h1>
              <p className="text-sm text-muted-foreground mt-1">
                Review records scheduled for deletion. Candidate tasks are created after retention expires.
              </p>
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => { pendingQuery.refetch(); historyQuery.refetch(); }}
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>

        {/* Stats strip */}
        <div className="rounded-2xl border border-border/60 bg-card shadow-sm overflow-hidden">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 sm:divide-x divide-border/60 divide-y sm:divide-y-0 lg:divide-y-0">
            <div className="flex items-center gap-3.5 px-5 py-4">
              <div className="h-9 w-9 rounded-xl bg-red-500/10 flex items-center justify-center shrink-0">
                <AlertTriangle className="h-4 w-4 text-red-600" />
              </div>
              <div className="min-w-0">
                <p className="text-2xl font-bold text-foreground leading-none">{totalExpired}</p>
                <p className="text-xs text-muted-foreground mt-0.5">Past retention</p>
              </div>
            </div>
            <div className="flex items-center gap-3.5 px-5 py-4">
              <div className="h-9 w-9 rounded-xl bg-amber-500/10 flex items-center justify-center shrink-0">
                <Clock className="h-4 w-4 text-amber-600" />
              </div>
              <div className="min-w-0">
                <p className="text-2xl font-bold text-foreground leading-none">{totalUpcoming}</p>
                <p className="text-xs text-muted-foreground mt-0.5">Approaching expiry</p>
              </div>
            </div>
            <div className="flex items-center gap-3.5 px-5 py-4">
              <div className="h-9 w-9 rounded-xl bg-slate-500/10 flex items-center justify-center shrink-0">
                <Trash2 className="h-4 w-4 text-slate-600" />
              </div>
              <div className="min-w-0">
                <p className="text-2xl font-bold text-foreground leading-none">{stats?.lastWeek ?? 0}</p>
                <p className="text-xs text-muted-foreground mt-0.5">Deleted last 7 days</p>
              </div>
            </div>
            <div className="flex items-center gap-3.5 px-5 py-4">
              <div className="h-9 w-9 rounded-xl bg-emerald-500/10 flex items-center justify-center shrink-0">
                <ShieldCheck className="h-4 w-4 text-emerald-600" />
              </div>
              <div className="min-w-0">
                <p className="text-2xl font-bold text-foreground leading-none">{stats?.lastMonth ?? 0}</p>
                <p className="text-xs text-muted-foreground mt-0.5">Deleted last 30 days</p>
              </div>
            </div>
          </div>
        </div>

        {/* Candidate Applications */}
        <div className="space-y-2">
          <div className="flex items-center gap-2 px-1">
            <UserRound className="w-4 h-4 text-muted-foreground" />
            <h2 className="text-sm font-semibold">Candidate applications</h2>
            <span className="text-xs text-muted-foreground">({apps.length} pending review)</span>
          </div>
          <div className="rounded-2xl border border-border/60 bg-card shadow-sm overflow-hidden">
            {pendingQuery.isLoading ? (
              <div className="p-4">
                <TableSkeleton rows={4} cols={4} />
              </div>
            ) : apps.length === 0 ? (
              <div className="text-sm text-muted-foreground px-4 py-6">
                No applications scheduled for deletion in the next 60 days.
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="border-b border-border/60 bg-muted/30 hover:bg-muted/30">
                    <TableHead className="pl-4 font-semibold text-xs text-muted-foreground uppercase tracking-wide">Candidate</TableHead>
                    <TableHead className="hidden md:table-cell font-semibold text-xs text-muted-foreground uppercase tracking-wide">Position</TableHead>
                    <TableHead className="hidden lg:table-cell font-semibold text-xs text-muted-foreground uppercase tracking-wide">Applied</TableHead>
                    <TableHead className="font-semibold text-xs text-muted-foreground uppercase tracking-wide">Status</TableHead>
                    <TableHead className="font-semibold text-xs text-muted-foreground uppercase tracking-wide text-right pr-4">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {apps.map((a) => (
                    <ApplicationRow
                      key={a.id}
                      app={a}
                      onExtend={() => setExtendDialog({
                        entityType: "candidate_application",
                        entityId: a.id,
                        label: `${a.first_name} ${a.last_name} — ${a.position_title ?? "Unknown role"}`,
                      })}
                      onDelete={() => setConfirmDelete({
                        entityType: "candidate_application",
                        entityId: a.id,
                        label: `${a.first_name} ${a.last_name} (${a.email})`,
                      })}
                    />
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        </div>

        {/* Employee Documents */}
        <div className="space-y-2">
          <div className="flex items-center gap-2 px-1">
            <FileText className="w-4 h-4 text-muted-foreground" />
            <h2 className="text-sm font-semibold">Employee documents</h2>
            <span className="text-xs text-muted-foreground">({docs.length} pending review)</span>
          </div>
          <div className="rounded-2xl border border-border/60 bg-card shadow-sm overflow-hidden">
            {pendingQuery.isLoading ? (
              <div className="p-4">
                <TableSkeleton rows={4} cols={4} />
              </div>
            ) : docs.length === 0 ? (
              <div className="text-sm text-muted-foreground px-4 py-6">
                No documents past their retention period or expiring within 60 days.
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="border-b border-border/60 bg-muted/30 hover:bg-muted/30">
                    <TableHead className="pl-4 font-semibold text-xs text-muted-foreground uppercase tracking-wide">Document</TableHead>
                    <TableHead className="hidden md:table-cell font-semibold text-xs text-muted-foreground uppercase tracking-wide">Employee</TableHead>
                    <TableHead className="hidden lg:table-cell font-semibold text-xs text-muted-foreground uppercase tracking-wide">Expires</TableHead>
                    <TableHead className="font-semibold text-xs text-muted-foreground uppercase tracking-wide">Status</TableHead>
                    <TableHead className="font-semibold text-xs text-muted-foreground uppercase tracking-wide text-right pr-4">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {docs.map((d) => (
                    <DocumentRow
                      key={d.id}
                      doc={d}
                      onExtend={() => setExtendDialog({
                        entityType: "document",
                        entityId: d.id,
                        label: `"${d.document_name}" — ${d.first_name ?? ""} ${d.last_name ?? ""}`.trim(),
                      })}
                      onDelete={() => setConfirmDelete({
                        entityType: "document",
                        entityId: d.id,
                        label: `"${d.document_name}" (${d.first_name ?? ""} ${d.last_name ?? ""})`,
                      })}
                    />
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        </div>

        {/* Deletion History */}
        <div className="space-y-2">
          <div className="flex items-center gap-2 px-1">
            <Trash2 className="w-4 h-4 text-muted-foreground" />
            <h2 className="text-sm font-semibold">Deletion history</h2>
            <span className="text-xs text-muted-foreground">(last {history.length} entries)</span>
          </div>
          <div className="rounded-2xl border border-border/60 bg-card shadow-sm overflow-hidden">
            {historyQuery.isLoading ? (
              <div className="p-4">
                <TableSkeleton rows={4} cols={4} />
              </div>
            ) : history.length === 0 ? (
              <div className="text-sm text-muted-foreground px-4 py-6">
                No deletions recorded yet.
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="border-b border-border/60 bg-muted/30 hover:bg-muted/30">
                    <TableHead className="pl-4 font-semibold text-xs text-muted-foreground uppercase tracking-wide">Type</TableHead>
                    <TableHead className="font-semibold text-xs text-muted-foreground uppercase tracking-wide">Detail</TableHead>
                    <TableHead className="hidden md:table-cell font-semibold text-xs text-muted-foreground uppercase tracking-wide">Triggered by</TableHead>
                    <TableHead className="hidden lg:table-cell font-semibold text-xs text-muted-foreground uppercase tracking-wide">When</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {history.map((h) => (
                    <HistoryRow key={h.id} entry={h} />
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        </div>
      </div>

      {/* Confirm delete dialog */}
      <Dialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-600" />
              Confirm immediate deletion
            </DialogTitle>
            <DialogDescription>
              This will permanently delete the record and all associated files from storage. This cannot be undone and will be recorded in the audit log.
            </DialogDescription>
          </DialogHeader>
          {confirmDelete && (
            <div className="bg-muted/50 rounded-md p-3 text-sm">
              <span className="font-medium">Target:</span> {confirmDelete.label}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDelete(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              disabled={deleteMutation.isPending}
              onClick={() => {
                if (confirmDelete) {
                  deleteMutation.mutate({
                    entityType: confirmDelete.entityType,
                    entityId: confirmDelete.entityId,
                  });
                }
              }}
            >
              <Trash2 className="w-4 h-4 mr-2" />
              {deleteMutation.isPending ? "Deleting…" : "Delete permanently"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Extend retention dialog */}
      <Dialog open={!!extendDialog} onOpenChange={(o) => !o && setExtendDialog(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CalendarPlus className="w-5 h-5 text-blue-600" />
              Extend retention period
            </DialogTitle>
            <DialogDescription>
              Push back the deletion date for this record. Useful for legal holds, ongoing reviews, or candidates being re-evaluated.
            </DialogDescription>
          </DialogHeader>
          {extendDialog && (
            <div className="space-y-4">
              <div className="bg-muted/50 rounded-md p-3 text-sm">
                <span className="font-medium">Target:</span> {extendDialog.label}
              </div>
              <div>
                <label className="text-sm font-medium block mb-1.5">Extend by (days)</label>
                <Input
                  type="number"
                  min={1}
                  max={365}
                  value={extendDays}
                  onChange={(e) => setExtendDays(parseInt(e.target.value) || 0)}
                />
                <p className="text-xs text-muted-foreground mt-1">Maximum 365 days per extension. Run again if more time is needed later.</p>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setExtendDialog(null)}>
              Cancel
            </Button>
            <Button
              disabled={extendMutation.isPending || extendDays < 1 || extendDays > 365}
              onClick={() => {
                if (extendDialog) {
                  extendMutation.mutate({
                    entityType: extendDialog.entityType,
                    entityId: extendDialog.entityId,
                    days: extendDays,
                  });
                }
              }}
            >
              <CalendarPlus className="w-4 h-4 mr-2" />
              {extendMutation.isPending ? "Extending…" : `Extend by ${extendDays} days`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppLayout>
  );
}

// ── Sub-components ───────────────────────────────────────────────────────────

function ApplicationRow({
  app, onExtend, onDelete,
}: {
  app: GdprPendingApplication;
  onExtend: () => void;
  onDelete: () => void;
}) {
  const appliedAt = new Date(app.applied_at);
  const deleteAt = new Date(app.gdpr_expiry);
  const daysUntil = differenceInDays(deleteAt, new Date());
  const initials = `${app.first_name?.[0] ?? ""}${app.last_name?.[0] ?? ""}`.toUpperCase() || "?";

  return (
    <TableRow className="border-b border-border/40 transition-colors hover:bg-muted/40">
      <TableCell className="pl-4 py-3">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-xs font-semibold text-primary shrink-0">
            {initials}
          </div>
          <div>
            <p className="font-medium text-sm">{app.first_name} {app.last_name}</p>
            <p className="text-xs text-muted-foreground">{app.email}</p>
          </div>
        </div>
      </TableCell>
      <TableCell className="hidden md:table-cell py-3 text-sm text-muted-foreground">
        {app.position_title ?? "—"}
      </TableCell>
      <TableCell className="hidden lg:table-cell py-3 text-sm text-muted-foreground">
        {format(appliedAt, "dd MMM yyyy")}
      </TableCell>
      <TableCell className="py-3">
        {app.expired ? (
          <Badge variant="outline" className="text-xs bg-red-100 text-red-700 border-red-200 gap-1">
            <AlertTriangle className="w-3 h-3" />
            Expired ({Math.abs(daysUntil)}d ago)
          </Badge>
        ) : (
          <Badge variant="outline" className="text-xs bg-amber-100 text-amber-700 border-amber-200 gap-1">
            <Clock className="w-3 h-3" />
            {daysUntil}d remaining
          </Badge>
        )}
      </TableCell>
      <TableCell className="py-3 pr-4">
        <div className="flex justify-end gap-1.5">
          <Button size="sm" variant="outline" onClick={onExtend}>
            <CalendarPlus className="w-3.5 h-3.5 mr-1.5" />
            Extend
          </Button>
          <Button size="sm" variant="destructive" onClick={onDelete} disabled={!app.expired}>
            <Trash2 className="w-3.5 h-3.5 mr-1.5" />
            Delete now
          </Button>
        </div>
      </TableCell>
    </TableRow>
  );
}

function DocumentRow({
  doc, onExtend, onDelete,
}: {
  doc: GdprPendingDocument;
  onExtend: () => void;
  onDelete: () => void;
}) {
  const expiry = new Date(doc.gdpr_expiry);
  const daysUntil = differenceInDays(expiry, new Date());
  const employeeName = doc.first_name || doc.last_name
    ? `${doc.first_name ?? ""} ${doc.last_name ?? ""}`.trim()
    : `Employee #${doc.employee_id}`;

  return (
    <TableRow className="border-b border-border/40 transition-colors hover:bg-muted/40">
      <TableCell className="pl-4 py-3">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-indigo-500/10 flex items-center justify-center shrink-0">
            <FileText className="w-4 h-4 text-indigo-600" />
          </div>
          <div>
            <p className="font-medium text-sm">{doc.document_name}</p>
            {doc.document_type && (
              <p className="text-xs text-muted-foreground">{doc.document_type}</p>
            )}
          </div>
        </div>
      </TableCell>
      <TableCell className="hidden md:table-cell py-3 text-sm text-muted-foreground">
        {employeeName}
      </TableCell>
      <TableCell className="hidden lg:table-cell py-3 text-sm text-muted-foreground">
        {format(expiry, "dd MMM yyyy")}
      </TableCell>
      <TableCell className="py-3">
        {doc.expired ? (
          <Badge variant="outline" className="text-xs bg-red-100 text-red-700 border-red-200 gap-1">
            <AlertTriangle className="w-3 h-3" />
            Expired ({Math.abs(daysUntil)}d ago)
          </Badge>
        ) : (
          <Badge variant="outline" className="text-xs bg-amber-100 text-amber-700 border-amber-200 gap-1">
            <Clock className="w-3 h-3" />
            {daysUntil}d remaining
          </Badge>
        )}
      </TableCell>
      <TableCell className="py-3 pr-4">
        <div className="flex justify-end gap-1.5">
          <Button size="sm" variant="outline" onClick={onExtend}>
            <CalendarPlus className="w-3.5 h-3.5 mr-1.5" />
            Extend
          </Button>
          <Button size="sm" variant="destructive" onClick={onDelete}>
            <Trash2 className="w-3.5 h-3.5 mr-1.5" />
            Delete now
          </Button>
        </div>
      </TableCell>
    </TableRow>
  );
}

function HistoryRow({ entry }: { entry: GdprHistoryEntry }) {
  const created = new Date(entry.createdAt);
  const kindLabel = entry.kind === "candidate" ? "Candidate" : entry.kind === "document" ? "Document" : "Other";
  const kindColor = entry.kind === "candidate"
    ? "bg-blue-100 text-blue-700 border-blue-200"
    : entry.kind === "document"
      ? "bg-indigo-100 text-indigo-700 border-indigo-200"
      : "bg-slate-100 text-slate-700 border-slate-200";

  return (
    <TableRow className="border-b border-border/40 transition-colors hover:bg-muted/40">
      <TableCell className="pl-4 py-3">
        <Badge variant="outline" className={`text-xs ${kindColor}`}>{kindLabel}</Badge>
      </TableCell>
      <TableCell className="py-3 text-sm text-muted-foreground max-w-md">
        <span className="truncate block" title={entry.message}>{entry.message}</span>
      </TableCell>
      <TableCell className="hidden md:table-cell py-3 text-sm">
        <span className="inline-flex items-center gap-1 text-muted-foreground">
          {entry.actorIsCron ? (
            <Bot className="w-3.5 h-3.5" />
          ) : (
            <Hand className="w-3.5 h-3.5" />
          )}
          {entry.actor}
        </span>
      </TableCell>
      <TableCell className="hidden lg:table-cell py-3 text-sm text-muted-foreground">
        <span title={format(created, "dd MMM yyyy HH:mm")}>
          {formatDistanceToNow(created, { addSuffix: true })}
        </span>
      </TableCell>
    </TableRow>
  );
}
